// SendCustomerDialog — JEDEN dialóg „Poslať zákazníkovi" pre CELÝ Matrix (ústava 103.D;
// Mirko 2026-07-27: „ideme jeden systém — ak si tu sadne užívateľ, nevie čo a ako, každá karta iná").
// Používajú ho: Obhliadky, Cenové ponuky, Objednávky. Vzhľad = pôvodný CP dialóg (referencia).
// Polia: Komu (editovateľné) · Kópia · Predmet · Text · príloha · poznámka · SMS checkbox.
// SMS po úspešnom odoslaní posiela dialóg sám cez /api/sms/send — jednotné správanie.
// V3 131.10 (Mirko 15. 9. 2026): pri Komu a Kópii Matrix ponúkne kontaktné osoby partnera (customerId); osoba vybraná
// do Komu doplní aj svoj telefón na SMS; nový kontakt partnera sa dá založiť priamo tu. bezSms = bez SMS časti (preposlanie dokladu).
import React, { useState, useEffect } from 'react'
import { COLORS, TYPO, SPACE, RADIUS, TINT } from '../theme.js'
import { Modal } from './Modal.jsx'
import { BtnAdd, BtnGhost } from './Btn.jsx'
import { useToast } from './Toast.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { pripravDokument } from '../hooks/unsavedGuard.js'   // p95 — doklad = obrazovka (ústava 67.15)
import { skontrolujVerziu } from './VersionWatcher.jsx'
import { KontaktyPonuka, usePartnerKontakty, cislaTel } from './KontaktyPartnera.jsx'

const inp = { width: '100%', padding: '7px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.base, fontFamily: 'inherit', boxSizing: 'border-box' }

import TelefonInput from './TelefonInput.jsx'

export default function SendCustomerDialog({
  open, onClose, title,
  initTo = '', initCc = '', phone = '',
  initSubject = '', initText = '',
  attachmentName = '', attachmentNote = 'vygeneruje sa automaticky.',
  extraOption = null,   // voliteľný uzol pod prílohou (napr. checkbox „priložiť protokol“) — Mirko 10.8.
  footNote = 'Kópia e-mailu ostane v odoslanej pošte firemnej schránky.',
  smsLabel = 'SMS, že mu bol dokument odoslaný',
  smsText = '', smsEntityType = '', smsEntityId = '',
  customerId = '',      // V3 131.10 — partner, ktorého kontaktné osoby sa ponúknu pri Komu a Kópii
  bezSms = false,       // V3 — dialóg bez SMS časti (napr. preposlanie došlej FA)
  komuLabel = 'Komu (e-mail zákazníka)',
  onSend,          // async ({to, cc, subject, text}) → throw pri chybe; návrat môže niesť {to}
  onSent,          // volá sa po úspechu (zatvor + reload v module)
}) {
  const toast = useToast()
  const [m, setM] = useState({ to: initTo, cc: initCc, subject: initSubject, text: initText, sms: !bezSms && !!phone, sending: false })
  // Telefon na SMS je EDITOVATELNY (Mirko 29.7.): predvyplni sa tym, co system najde,
  // a ked nenajde nic, da sa dopisat rukou — dialog uz SMS neodmietne natvrdo.
  const [telNaSms, setTelNaSms] = useState(phone)
  useEffect(() => { if (open) { setM({ to: initTo, cc: initCc, subject: initSubject, text: initText, sms: !bezSms && !!phone, sending: false }); setTelNaSms(phone) } }, [open])  // eslint-disable-line
  const kontakty = usePartnerKontakty(open ? customerId : '')

  if (!open) return null
  const set = (k, v) => setM(s => ({ ...s, [k]: v }))
  const majitelTel = cislaTel(telNaSms).length === 9 ? kontakty.rows.find(k => cislaTel(k.telefon) === cislaTel(telNaSms)) : null

  const send = async () => {
    if (!String(m.to).trim()) { toast.error('Zadaj e-mail príjemcu.'); return }
    // p95 DOKLAD = OBRAZOVKA (ústava 67.15): stará karta po nasadení nič neodošle; neuložené zmeny sa najprv uložia,
    // a keď sa uložiť nedajú, doklad NEODÍDE.
    if (!(await skontrolujVerziu())) { toast.error('Matrix bol medzitým aktualizovaný — obnov stránku (Obnoviť teraz vľavo dole) a odošli znova. Z tejto karty sa doklad neodošle.'); return }
    if (!(await pripravDokument(toast))) return
    set('sending', true)
    try {
      const r = await onSend({ to: m.to.trim(), cc: String(m.cc || '').trim(), subject: m.subject, text: m.text })
      toast.success('Odoslané na ' + ((r && r.to) || m.to.trim()))
      // 103.K (Mirko 6.8.): ak SERVER poslal SMS + zapísal vlákno sám (CP), neposielaj druhú — len ukáž výsledok.
      if (r && r.sms) {
        if (r.sms.ok) toast.success('Informačná SMS odoslaná na ' + r.sms.to + ' + zapísané do vlákna.')
        else if (r.sms.skip) toast.info('SMS sa neposlala: ' + r.sms.skip + '.')
        else if (r.sms.fail) toast.error('E-mail OK, ale SMS zlyhala: ' + r.sms.fail)
      } else if (!bezSms && m.sms && telNaSms && smsText) {
        try {
          const sr = await fetchAuth('/api/sms/send', { method: 'POST', body: JSON.stringify({ to: telNaSms, text: smsText, entity_type: smsEntityType, entity_id: smsEntityId }) })
          const sd = await sr.json().catch(() => ({}))
          if (sr.ok && sd.ok) toast.success('Informatívna SMS odoslaná na ' + telNaSms)
          else toast.error('SMS sa neodoslala: ' + (sd.error || 'chyba brány'))
        } catch (_) { toast.error('SMS sa neodoslala.') }
      }
      onSent && onSent()
    } catch (e) {
      toast.error(e.message || 'Odoslanie zlyhalo')
      set('sending', false)
    }
  }

  return (
    <Modal open onClose={onClose} title={title} maxWidth={620}
      footer={<>
        <BtnGhost onClick={onClose} disabled={m.sending}>Zrušiť</BtnGhost>
        {m.sending && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, alignSelf: 'center', marginRight: 'auto' }}>Môže to trvať aj minútu — nezatváraj okno. Keď to nevyjde, povieme to.</span>}
        {/* posta_cas_v1: tvorba PDF trvá aj minútu. Keď tlačidlo mlčí, človek si myslí, že je to zaseknuté, a klikne znova. */}
        <BtnAdd onClick={send} disabled={m.sending || !m.to}>{m.sending ? 'Pripravujem PDF a posielam…' : 'Odoslať'}</BtnAdd>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        <div>
          <label style={{ fontSize: TYPO.sizes.sm, display: 'block' }}>
            <div style={{ color: COLORS.muted, marginBottom: 4 }}>{komuLabel}</div>
            <input value={m.to} onChange={e => set('to', e.target.value)} placeholder="zakaznik@firma.sk" style={inp} />
          </label>
          <KontaktyPonuka customerId={customerId} hodnota={m.to} popis="Komu" predvolenyTelefon={bezSms ? '' : telNaSms}
            onVyber={(k) => { set('to', k.email); if (!bezSms && k.telefon) { setTelNaSms(k.telefon); set('sms', true) } }} />
        </div>
        <div>
          <label style={{ fontSize: TYPO.sizes.sm, display: 'block' }}>
            <div style={{ color: COLORS.muted, marginBottom: 4 }}>Kópia (voliteľné — druhý e-mail)</div>
            <input value={m.cc} onChange={e => set('cc', e.target.value)} placeholder="napr. druhý kontakt na stavbe" style={inp} />
          </label>
          <KontaktyPonuka customerId={customerId} hodnota={m.cc} popis="Kópia" sNovym={false}
            onVyber={(k, uzJe) => set('cc', uzJe ? '' : k.email)} />
        </div>
        <label style={{ fontSize: TYPO.sizes.sm }}>
          <div style={{ color: COLORS.muted, marginBottom: 4 }}>Predmet</div>
          <input value={m.subject} onChange={e => set('subject', e.target.value)} style={inp} />
        </label>
        <label style={{ fontSize: TYPO.sizes.sm }}>
          <div style={{ color: COLORS.muted, marginBottom: 4 }}>Text e-mailu</div>
          <textarea value={m.text} onChange={e => set('text', e.target.value)} rows={8} style={{ ...inp, resize: 'vertical' }} />
        </label>
        {attachmentName && (
          <div style={{ fontSize: TYPO.sizes.sm, padding: '6px 10px', background: TINT.nav, borderRadius: RADIUS.sm }}>
            Príloha: <strong>{attachmentName}</strong> — {attachmentNote}
          </div>
        )}
        {extraOption}
        {footNote && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{footNote}</div>}
        {!bezSms && (
          <div style={{ padding: '8px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: RADIUS.sm }}>
            <label style={{ fontSize: TYPO.sizes.sm, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={m.sms} disabled={!telNaSms} onChange={e => set('sms', e.target.checked)} />
              <span>Poslať zákazníkovi aj <b>{smsLabel}</b></span>
            </label>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, paddingTop: 7, whiteSpace: 'nowrap' }}>Číslo na SMS</span>
              <div style={{ flex: 1 }}>
                <TelefonInput value={telNaSms} onChange={v => { setTelNaSms(v); if (v) set('sms', true) }}
                  style={{ width: '100%', padding: '6px 9px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box' }} />
                {majitelTel && <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.nav, marginTop: 3 }}>Telefón osoby {majitelTel.meno || majitelTel.email}{majitelTel.funkcia ? ' · ' + majitelTel.funkcia : ''}</div>}
              </div>
            </div>
            {!telNaSms && (
              <div style={{ fontSize: TYPO.sizes.xs, color: '#b45309', marginTop: 4 }}>
                Číslo nemáme ani na doklade, ani na karte zákazníka — dopíš ho sem a SMS odíde. Na kartu sa tým nezapíše.
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
